import React, { useState } from 'react';
import { Box, VStack, HStack, Text, Button, Spinner, Checkbox, Link } from '@chakra-ui/react';
import { Trans, useTranslation } from 'react-i18next';
import { HiOutlineShieldCheck, HiOutlineCpuChip } from 'react-icons/hi2';

const stripTags = (s) => s.replace(/<\/?\d+>/g, '');

const ConsentCheckbox = ({ checked, onChange, colors, children }) => (
    <Checkbox.Root
        checked={checked}
        onCheckedChange={(e) => onChange(!!e.checked)}
        alignItems="flex-start"
        gap={3}
        w="full"
        p={3}
        borderRadius="12px"
        borderWidth="1px"
        style={{ borderColor: checked ? colors.avatarBg : colors.borderColor }}
        cursor="pointer"
    >
        <Checkbox.HiddenInput />
        <Checkbox.Control
            mt="2px"
            flexShrink={0}
            borderRadius="5px"
            style={checked
                ? { background: colors.avatarBg, borderColor: colors.avatarBg, color: colors.onAccent }
                : { borderColor: colors.textSecondary }}
        >
            <Checkbox.Indicator />
        </Checkbox.Control>
        <Checkbox.Label
            fontSize="sm"
            lineHeight="1.55"
            fontWeight="normal"
            whiteSpace="pre-wrap"
            style={{ color: colors.textPrimary }}
        >
            {children}
        </Checkbox.Label>
    </Checkbox.Root>
);

// The bot keeps connecting underneath; the chat is blocked until both consents are accepted.
const ConsentOverlay = ({ colors, settings, isLoading, recruitmentId, onAccept }) => {
    const { t, i18n } = useTranslation('chat');
    const [gdpr, setGdpr] = useState(false);
    const [privacy, setPrivacy] = useState(false);
    const [customAnswers, setCustomAnswers] = useState({});

    const company = settings?.companyName || t('consent.companyFallback');
    const gdprText = settings?.gdprConsentText || t('consent.defaultGdpr', { company });
    const customPrivacyLabel = settings?.privacyPolicyConsentText;
    // i18n text is only a fallback for the server's versioned notice.
    const aiNotice = settings?.aiDisclosureText || t('consent.aiNotice');
    const customConsents = settings?.customConsents ?? [];
    const privacyEnabled = settings?.privacyPolicyEnabled !== false;
    const privacyRequired = settings?.privacyPolicyRequired !== false;
    const policyHref = `/privacy/${recruitmentId}`;

    const policyLink = (
        <Link
            href={policyHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            fontWeight="600"
            textDecoration="underline"
            style={{ color: colors.avatarBg }}
        />
    );

    const handleAccept = () => {
        onAccept({
            gdprAccepted: gdpr,
            privacyPolicyAccepted: privacyEnabled && privacy,
            gdprConsentText: gdprText,
            privacyPolicyConsentText: customPrivacyLabel || stripTags(t('consent.defaultPrivacyLabel')),
            language: i18n.language,
            gdprVersion: settings?.gdprVersion,
            privacyPolicyVersion: settings?.privacyPolicyVersion,
            customConsents: customConsents.map((c) => ({ id: c.id, accepted: Boolean(customAnswers[c.id]), version: c.version })),
        });
    };

    return (
        <Box
            position="fixed"
            inset="0"
            zIndex={1400}
            display="flex"
            alignItems={{ base: 'flex-end', md: 'center' }}
            justifyContent="center"
            p={{ base: 0, md: 6 }}
            bg="blackAlpha.500"
            backdropFilter="blur(4px)"
            role="dialog"
            aria-modal="true"
            aria-labelledby="consent-title"
        >
            <Box
                w="full"
                maxW="520px"
                maxH={{ base: '92dvh', md: '85vh' }}
                overflowY="auto"
                borderTopRadius="20px"
                borderBottomRadius={{ base: 0, md: '20px' }}
                p={{ base: 5, md: 7 }}
                boxShadow="0 20px 60px rgba(0,0,0,0.25)"
                style={{
                    background: colors.chatBg,
                    color: colors.textPrimary,
                    paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
                }}
            >
                {isLoading ? (
                    <HStack justify="center" py={10}>
                        <Spinner color={colors.avatarBg} />
                    </HStack>
                ) : (
                    <VStack align="stretch" gap={5}>
                        <VStack align="flex-start" gap={2}>
                            <Box
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                w="40px"
                                h="40px"
                                borderRadius="12px"
                                fontSize="22px"
                                style={{ background: `${colors.avatarBg}1A`, color: colors.avatarBg }}
                            >
                                <HiOutlineShieldCheck />
                            </Box>
                            <Text id="consent-title" fontSize="lg" fontWeight="700">
                                {t('consent.title')}
                            </Text>
                            <Text fontSize="sm" lineHeight="1.5" style={{ color: colors.textSecondary }}>
                                {t('consent.intro')}
                            </Text>
                        </VStack>

                        <HStack
                            align="flex-start"
                            gap={3}
                            p={3}
                            borderRadius="12px"
                            borderWidth="1px"
                            role="note"
                            style={{ borderColor: colors.avatarBg, background: `${colors.avatarBg}0F` }}
                        >
                            <Box fontSize="20px" mt="1px" flexShrink={0} style={{ color: colors.avatarBg }}>
                                <HiOutlineCpuChip />
                            </Box>
                            <Text fontSize="sm" lineHeight="1.55" fontWeight="600">
                                {aiNotice}
                            </Text>
                        </HStack>

                        <VStack align="stretch" gap={3}>
                            <ConsentCheckbox checked={gdpr} onChange={setGdpr} colors={colors}>
                                {gdprText}
                                <Text as="span" style={{ color: colors.avatarBg }}> *</Text>
                            </ConsentCheckbox>

                            {privacyEnabled && (
                                <ConsentCheckbox checked={privacy} onChange={setPrivacy} colors={colors}>
                                    {customPrivacyLabel ? (
                                        <>
                                            {customPrivacyLabel}{' '}
                                            {React.cloneElement(policyLink, {}, t('consent.policyLinkText'))}
                                        </>
                                    ) : (
                                        <Trans t={t} i18nKey="consent.defaultPrivacyLabel" components={{ 1: policyLink }} />
                                )}
                                {privacyRequired && <Text as="span" style={{ color: colors.avatarBg }}> *</Text>}
                            </ConsentCheckbox>
                            )}

                            {customConsents.map((c) => (
                                <ConsentCheckbox
                                    key={c.id}
                                    checked={Boolean(customAnswers[c.id])}
                                    onChange={(v) => setCustomAnswers((a) => ({ ...a, [c.id]: v }))}
                                    colors={colors}
                                >
                                    {c.text}
                                    {c.required && <Text as="span" style={{ color: colors.avatarBg }}> *</Text>}
                                </ConsentCheckbox>
                            ))}
                        </VStack>

                        <Text fontSize="xs" style={{ color: colors.textSecondary }}>
                            {t('consent.requiredNote')}
                        </Text>

                        <Button
                            w="full"
                            h="48px"
                            borderRadius="12px"
                            fontWeight="600"
                            color={colors.onAccent}
                            disabled={!gdpr || (privacyEnabled && privacyRequired && !privacy) || customConsents.some((c) => c.required && !customAnswers[c.id])}
                            onClick={handleAccept}
                            style={{ background: colors.avatarBg }}
                            _hover={{ opacity: 0.9 }}
                            _disabled={{ opacity: 0.4, cursor: 'not-allowed' }}
                        >
                            {t('consent.acceptButton')}
                        </Button>
                    </VStack>
                )}
            </Box>
        </Box>
    );
};

export default ConsentOverlay;
